import { samplingMoment } from '@/domain/enums/sample';
import { timezones } from '@/constants/localizer';

const roomTemperatureRange = { min: -1000, max: 1000 };
const sampleTemperatureRange = { min: -1000, max: 1000 };

export default {
  fields(vm) {
    return {
      primaryFields: [
        {
          model: 'color',
          label: 'fields.physical-chemical-analysis.color',
          tag: 's-number-input',
          decimals: 2,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 11,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'neutralizationIndex',
          label: 'fields.physical-chemical-analysis.neutralization-index',
          tag: 's-number-input',
          decimals: 3,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 12,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'interfacialTension25',
          label: 'fields.physical-chemical-analysis.interfacial-tension',
          tag: 's-number-input',
          decimals: 3,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 12,
          },
          temperature: 25,
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'relativeDensity',
          label: 'fields.physical-chemical-analysis.density',
          temperature: '20/4',
          tag: 's-number-input',
          decimals: 3,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 12,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'dissipationFactor25',
          label: 'fields.physical-chemical-analysis.dissipation-factor',
          temperature: 25,
          tag: 's-number-input',
          decimals: 3,
          validation: true,
          minValue: 0,
          maxValue: 100,
          optionalSettings: {
            maxLength: 7,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'dissipationFactor90',
          label: 'fields.physical-chemical-analysis.dissipation-factor',
          temperature: 90,
          tag: 's-number-input',
          decimals: 3,
          validation: true,
          minValue: 0,
          maxValue: 100,
          optionalSettings: {
            maxLength: 7,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'dissipationFactor100',
          label: 'fields.physical-chemical-analysis.dissipation-factor',
          temperature: 100,
          tag: 's-number-input',
          decimals: 3,
          validation: true,
          optionalSettings: {
            maxLength: 7,
          },
          minValue: 0,
          maxValue: 100,
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'dielectricStrength',
          label: 'fields.physical-chemical-analysis.dielectric-strength',
          tag: 's-number-input',
          decimals: 2,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 11,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'waterContent',
          label: 'fields.physical-chemical-analysis.water-content',
          tag: 's-number-input',
          decimals: 1,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 10,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'waterContent20',
          label: 'fields.physical-chemical-analysis.corrected-water-content',
          temperature: 20,
          tag: 's-number-input',
          decimals: 1,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 10,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'visualAspect',
          label: 'fields.physical-chemical-analysis.visual-aspect',
          tag: 's-input-label-edit',
          optionalSettings: {
            maxLength: 150,
          },
          type: 'text',
          validation: false,
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 8,
          },
        },
      ],
      secondaryFields: [
        {
          model: 'sediments',
          label: 'fields.physical-chemical-analysis.sediments',
          tag: 's-select',
          validation: false,
          options: [
            {
              value: null,
              text: vm.$t('multiselect.placeholder'),
            },
            {
              value: true,
              text: vm.$t('enums.sediment.with-sediments'),
            },
            {
              value: false,
              text: vm.$t('enums.sediment.without-sediments'),
            },
          ],
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'dbpcInhibitorContent',
          label: 'fields.physical-chemical-analysis.inhibitor-content',
          tag: 's-number-input',
          decimals: 2,
          validation: true,
          minValue: 0,
          maxValue: 100,
          optionalSettings: {
            maxLength: 6,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'pcbContent',
          label: 'fields.physical-chemical-analysis.pcb-content',
          tag: 's-number-input',
          decimals: 2,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 11,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'rbot',
          label: 'fields.physical-chemical-analysis.rbot',
          tag: 's-number-input',
          decimals: 1,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 10,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'furfuraldehydeContent',
          label: 'fields.physical-chemical-analysis.furfuraldehyde',
          tag: 's-number-input',
          decimals: 2,
          validation: true,
          optionalSettings: {
            maxLength: 11,
          },
          minValue: 0,
          maxValue: 1000000,
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'flashPoint',
          label: 'fields.physical-chemical-analysis.flash-point',
          tag: 's-number-input',
          decimals: 1,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 10,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'passivatorContent',
          label: 'fields.physical-chemical-analysis.passivator-content',
          tag: 's-number-input',
          decimals: 2,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 11,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'particlesCount',
          label: 'fields.physical-chemical-analysis.particle-count',
          tag: 's-number-input',
          decimals: 1,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 10,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'resistivity20',
          label: 'fields.physical-chemical-analysis.resistivity',
          tag: 's-number-input',
          decimals: 2,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 11,
          },
          temperature: 20,
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'resistivity90',
          label: 'fields.physical-chemical-analysis.resistivity',
          tag: 's-number-input',
          decimals: 2,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 11,
          },
          temperature: 90,
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'dbdsContent',
          label: 'fields.physical-chemical-analysis.dibenzyldisulfide-content',
          tag: 's-number-input',
          decimals: 3,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 12,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'corrosiveSulfur',
          label: 'fields.physical-chemical-analysis.corrosive-sulfur',
          tag: 's-select',
          validation: false,
          options: [
            {
              value: null,
              text: vm.$t('multiselect.placeholder'),
            },
            {
              value: true,
              text: vm.$t('enums.sulfur.corrosive'),
            },
            {
              value: false,
              text: vm.$t('enums.sulfur.non-corrosive'),
            },
          ],
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'totalDissolvedGasesInOil',
          label: 'fields.physical-chemical-analysis.dissolved-gases',
          tag: 's-number-input',
          decimals: 1,
          validation: true,
          minValue: 0,
          maxValue: 1000000,
          optionalSettings: {
            maxLength: 10,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
      ],
      extraFields: [
        {
          model: 'oilSaturation',
          label: 'fields.physical-chemical-analysis.oil-saturation',
          tag: 's-number-input',
          decimals: 1,
          validation: true,
          minValue: 0,
          maxValue: 100,
          optionalSettings: {
            maxLength: 5,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'paperSaturation',
          label: 'fields.physical-chemical-analysis.paper-saturation',
          tag: 's-number-input',
          decimals: 1,
          validation: true,
          minValue: 0,
          maxValue: 100,
          optionalSettings: {
            maxLength: 5,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 8,
          },
        },
        {
          model: 'transformerOilSamplingLocation',
          label: 'fields.physical-chemical-analysis.oil-sampling-location',
          tag: 's-input-label-edit',
          type: 'textarea',
          optionalSettings: {
            maxLength: 500,
          },
          validation: false,
          breakpoint: {
            cols: 12,
          },
        },
        {
          model: 'sampleTemperature',
          label: 'fields.physical-chemical-analysis.sample-temperature',
          tag: 's-number-input',
          decimals: 1,
          validation: true,
          minValue: sampleTemperatureRange.min,
          maxValue: sampleTemperatureRange.max,
          optionalSettings: {
            maxLength: 7,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'roomTemperature',
          label: 'fields.physical-chemical-analysis.ambient-temperature',
          tag: 's-number-input',
          decimals: 1,
          validation: true,
          minValue: roomTemperatureRange.min,
          maxValue: roomTemperatureRange.max,
          optionalSettings: {
            maxLength: 7,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'samplingMoment',
          label: 'fields.physical-chemical-analysis.sampling-moment',
          tag: 's-select',
          validation: false,
          isRequired: true,
          options: [
            {
              value: samplingMoment.BEFORE_FILLING,
              text: vm.$t('enums.sampling-moment.before-filling'),
            },
            {
              value: samplingMoment.AFTER_FILLING,
              text: vm.$t('enums.sampling-moment.after-filling'),
            },
          ],
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'airRelativeHumidity',
          label: 'fields.physical-chemical-analysis.relative-humidity',
          tag: 's-number-input',
          decimals: 1,
          validation: true,
          minValue: 0,
          maxValue: 100,
          optionalSettings: {
            maxLength: 5,
          },
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          model: 'analyzeDate',
          label: 'fields.physical-chemical-analysis.analysis-date',
          tag: 's-input-date-picker',
          validation: false,
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
          timeZone: timezones.UTC,
        },
        {
          model: 'analyzeTime',
          label: 'fields.physical-chemical-analysis.analysis-hour',
          tag: 's-input-time',
          validation: false,
          breakpoint: {
            cols: 12,
            lg: 6,
            xl: 4,
          },
        },
        {
          label: 'fields.general.observations',
          model: 'observation',
          tag: 's-input-label-edit',
          optionalSettings: {
            maxLength: 500,
          },
          type: 'textarea',
          validation: false,
          breakpoint: {
            cols: 12,
            md: 12,
            lg: 12,
          },
        },
      ],
    };
  },
};
